/**
 * A filtered collection, drawn as a table or as cards: the one way to render a
 * data table.
 *
 * The pattern is one shape everywhere, a filter row carrying the count and the
 * view controls, then the results, and it was being rebuilt per screen, so
 * each list drifted: the count in two places saying two different things, the
 * toggle on a row of its own, a table still rendering on a phone. Owning the
 * composition here is what makes those decisions once.
 *
 * Cards are derived from the columns, so a screen writes no second rendering.
 * Below `md` a collection is cards only; a table stays a table there only when
 * the screen says why with `tableOnSmall`.
 *
 * @module components/ui/CollectionView
 */

import React, { type ReactNode, useId, useState } from "react";

import { IconButton } from "./Button";
import { DerivedCard } from "./CollectionCards";
import {
  type Column,
  DataTable,
  orderRows,
  type RowExpansion,
  type RowGroup,
  type SortState,
} from "./DataTable";
import { FilterBar } from "./FilterBar";
import { SelectField } from "./Form";
import { RowActionControls, type RowActionsSpec } from "./RowActions";
import { type ListMode, ListToggle, useCardsOnlyViewport, useListMode } from "./SegmentedControl";
import { CardBones } from "./SkeletonParts";
import { VIRTUALIZE_AFTER } from "./VirtualTableBody";

/** The filter panel. Absent means the collection is not filtered. */
export interface CollectionFiltering {
  /** Names the panel and heads it, e.g. `Filter programmes`. */
  label: string;
  controls: ReactNode;
  /** How much of the list is showing. Rides on the summary, not the controls. */
  count?: string;
  /** Names the visible count, for a screen whose spec already reads it. */
  countTestId?: string;
  /** Whether any filter is narrowing the list, for the clear control. */
  active: boolean;
  onClear: () => void;
  /** Collapsed unless the screen has a reason to open it. */
  defaultOpen?: boolean;
  "data-testid"?: string;
}

/**
 * A card rendering of the screen's own, for a row the derived card cannot lay
 * out. Absent means the card is derived from the columns.
 */
export interface CollectionCards<Row> {
  render: (row: Row) => ReactNode;
  "data-testid"?: string;
}

/**
 * The filter panel on its own, for a screen whose results are not a table.
 *
 * `CollectionView` renders this; a screen that filters something else — a
 * picker, a matrix — renders it directly so the accordion, the column rules
 * and the clear row stay one decision.
 */
export function FilterPanel({
  filtering,
  trailing = undefined,
  variant = undefined,
}: {
  filtering: CollectionFiltering;
  /** Controls on the panel's header row, such as the view toggle. */
  trailing?: ReactNode;
  variant?: "card" | "flush";
}): React.ReactElement {
  return (
    <FilterBar
      trailing={trailing}
      variant={variant}
      aria-label={filtering.label}
      summary={filtering.label}
      count={filtering.count}
      countTestId={filtering.countTestId}
      active={filtering.active}
      onClear={filtering.onClear}
      defaultOpen={filtering.defaultOpen}
      data-testid={filtering["data-testid"]}
    >
      {filtering.controls}
    </FilterBar>
  );
}

type RowSource<Row> =
  { rows: readonly Row[]; groups?: never } | { groups: readonly RowGroup<Row>[]; rows?: never };

export type CollectionViewProps<Row> = RowSource<Row> & {
  /** Identifies the list, so its view mode is remembered per list. */
  storageKey: string;
  columns: readonly Column<Row>[];
  rowKey: (row: Row) => string;
  caption: string;
  /** Shows the table's caption rather than keeping it for screen readers. */
  showCaption?: boolean;
  /** Shown in place of the rows, in either mode. */
  emptyState: ReactNode;
  /**
   * Draws skeleton rows in place of the rows while a read is outstanding.
   * `"silent"` for a caller whose own region already announces the wait.
   */
  loading?: boolean | "silent";
  /** Names the wait; defaults to the caption's. */
  loadingLabel?: string;
  /** Shown in place of the rows when the read failed, with its own retry. */
  error?: ReactNode;
  /** A card of the screen's own. Absent means one derived from `columns`. */
  cards?: CollectionCards<Row>;
  filtering?: CollectionFiltering;
  /**
   * What each row can do, drawn as a final column in the table and as the
   * card's footer. Replaces any action column the screen would write itself.
   */
  rowActions?: RowActionsSpec<Row>;
  /** Makes the whole card open the row. The table keeps its own link. */
  onOpenRow?: (row: Row) => void;
  /**
   * Keeps the table below `md`, where a collection is otherwise cards only.
   * The reason is required and is what a reviewer reads to judge it: a grid
   * whose meaning is its rows and columns together, say, which a stack of cards
   * would take apart.
   */
  tableOnSmall?: { reason: string };
  /** Extra classes for a table row. Cards carry the same signal in their own content. */
  rowClassName?: (row: Row) => string;
  /**
   * Whether the Table/Cards toggle is offered. Left on, it is on every
   * collection; a reference list that is only ever read as a table turns it off.
   */
  viewToggle?: boolean;
  /** Held by the view when the screen does not hold it itself. */
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** `external` draws and reports the sort without reordering the rows. */
  sortMode?: "client" | "external";
  density?: "sm" | "md";
  /** Passed to the table. `top` for rows whose cells hold a paragraph. */
  verticalAlign?: "top" | "middle";
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  rowTestId?: (row: Row) => string;
  /**
   * Opens a row in place, for a detail that loads on expand.
   *
   * Table mode only. In cards mode the card renderer already receives the
   * row, and it owns the same `expandedKeys` the caller passes here, so it
   * draws its own disclosure rather than taking one it cannot lay out.
   */
  expansion?: RowExpansion<Row>;
  /** The mode to use before anyone chooses one. */
  initialMode?: ListMode;
  /**
   * Names the table, not the wrapper: a screen asking whether the table is
   * showing means the table, and a wrapper carrying the id answers yes in
   * card mode too.
   */
  /** Names the card list and each derived card, for a screen whose specs read them. */
  cardsTestId?: string;
  cardTestId?: (row: Row) => string;
  /** On the wrapper, for spacing the screen owns. */
  className?: string;
  "data-testid"?: string;
};

/** The columns a reader may sort the cards by. */
function sortableColumns<Row>(columns: readonly Column<Row>[]): readonly Column<Row>[] {
  return columns.filter((column) => column.sortable ?? Boolean(column.sortValue));
}

interface SortByProps<Row> {
  columns: readonly Column<Row>[];
  sort: SortState | undefined;
  onChange: (sort: SortState) => void;
}

/** Cards have no column headers to press, so the sort is a control of its own. */
function SortBy<Row>({ columns, sort, onChange }: SortByProps<Row>): React.ReactElement {
  const id = useId();
  const descending = sort?.direction === "desc";
  return (
    <div className="flex items-center gap-1">
      <label htmlFor={id} className="sr-only">
        Sort by
      </label>
      <SelectField
        id={id}
        value={sort?.key ?? ""}
        placeholder="Sort by"
        size="sm"
        className="w-40"
        options={columns.map((column) => ({ value: column.key, label: column.header }))}
        onChange={(key) => {
          onChange({ key, direction: sort?.direction ?? "asc" });
        }}
        data-testid="collection-sort"
      />
      <IconButton
        icon={descending ? "sort-descending" : "sort-ascending"}
        size="sm"
        aria-label={
          descending
            ? "Sorted descending, press for ascending"
            : "Sorted ascending, press for descending"
        }
        disabled={sort === undefined}
        onClick={() => {
          if (sort) {
            onChange({ key: sort.key, direction: descending ? "asc" : "desc" });
          }
        }}
        data-testid="collection-sort-direction"
      />
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <CollectionView storageKey="programmes" columns={columns} rows={rows}
 *   rowKey={(p) => p.id} caption="Programmes you can reach" emptyState={empty}
 *   filtering={filtering} rowActions={actions} />
 * ```
 */
export function CollectionView<Row>({
  storageKey,
  columns,
  rowKey,
  caption,
  showCaption = false,
  emptyState,
  loading = false,
  loadingLabel = undefined,
  error = undefined,
  cards = undefined,
  filtering = undefined,
  rowActions = undefined,
  onOpenRow = undefined,
  tableOnSmall = undefined,
  sortMode = undefined,
  density = undefined,
  verticalAlign = undefined,
  stickyHeader = undefined,
  stickyFirstColumn = undefined,
  rowTestId = undefined,
  rowClassName = undefined,
  viewToggle = true,
  expansion = undefined,
  initialMode = "table",
  rows = undefined,
  groups = undefined,
  className = "",
  cardsTestId = undefined,
  cardTestId = undefined,
  sort: sortProp = undefined,
  onSortChange: onSortChangeProp = undefined,
  "data-testid": testId,
}: CollectionViewProps<Row>): React.ReactElement {
  const [chosen, setMode] = useListMode(storageKey, initialMode);
  const narrow = useCardsOnlyViewport();
  // Held here when the screen does not hold it, so every list with a sortable
  // column sorts, in the table and in the cards, without each writing the pair.
  const [ownSort, setOwnSort] = useState<SortState | undefined>(undefined);
  const sort = sortProp ?? ownSort;
  const onSortChange = onSortChangeProp ?? setOwnSort;

  // `useListMode` forces cards below `md`; a table that is allowed to stay
  // takes that back.
  const mode: ListMode = narrow && tableOnSmall ? "table" : chosen;
  const showCards = mode === "cards";
  const sortable = sortableColumns(columns);
  const showSort = showCards && sortable.length > 0;

  // The row's own actions are one more column in the table, and the card's
  // footer in cards, so the two cannot disagree about what a row can do.
  const tableColumns: readonly Column<Row>[] = rowActions
    ? [
        ...columns,
        {
          key: "row-actions",
          header: "Actions",
          hideHeader: true,
          align: "end",
          width: "w-px",
          render: (row) => <RowActionControls row={row} spec={rowActions} />,
        },
      ]
    : columns;

  const sections: readonly RowGroup<Row>[] = groups ?? [
    { key: "", header: null, rows: rows ?? [] },
  ];
  const total = sections.reduce((count, group) => count + group.rows.length, 0);

  const trailing = (
    <>
      {showSort && <SortBy columns={sortable} sort={sort} onChange={onSortChange} />}
      {viewToggle && <ListToggle value={mode} onChange={setMode} />}
    </>
  );
  const toolbar = filtering ? (
    <FilterPanel filtering={filtering} trailing={trailing} />
  ) : (
    (!narrow || showSort) && (
      <div className="flex flex-wrap items-center justify-end gap-2">{trailing}</div>
    )
  );

  let body: ReactNode = null;
  if (error) {
    body = error;
  } else if (showCards && loading) {
    body = (
      <CardBones
        count={3}
        label={
          loading === "silent" ? undefined : (loadingLabel ?? `Loading ${caption.toLowerCase()}`)
        }
      />
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`.trim()}>
      {toolbar}

      {body ??
        (showCards && !loading ? (
          <div className="flex flex-col gap-3">
            {sections.map((group) => (
              <React.Fragment key={group.key}>
                {group.header !== null && (
                  <div className="border-b border-base-300 pb-2 font-semibold">{group.header}</div>
                )}
                <div
                  // Named and listed, because the table's `caption` is what names
                  // the collection and cards mode had neither: on a phone, where
                  // the mode is forced, it was an unnamed div of loose articles.
                  role="list"
                  aria-label={group.header === null ? caption : `${caption}, ${group.key}`}
                  className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
                  data-testid={cards?.["data-testid"] ?? cardsTestId}
                >
                  {total === 0 ? (
                    // Spanning, or the empty state is one track of the grid and
                    // paints at a third of the width.
                    <div className="col-span-full">{emptyState}</div>
                  ) : null}
                  {/* The order the table would draw: the sort survives the switch
                    to cards, and below `md` cards are the only rendering. */}
                  {orderRows(group.rows, { columns, sort, sortMode, onSortChange }).map((row) => (
                    // `min-w-0`: a grid item defaults to `min-width: auto`, so a
                    // wide child widens the track rather than wrapping inside it.
                    <div
                      role="listitem"
                      key={rowKey(row)}
                      // A long list skips laying out and painting the cards
                      // off screen; the browser's own windowing, for divs.
                      className={`min-w-0 ${total > VIRTUALIZE_AFTER ? "[contain-intrinsic-size:auto_12rem] [content-visibility:auto]" : ""}`.trim()}
                    >
                      {cards ? (
                        cards.render(row)
                      ) : (
                        <DerivedCard
                          row={row}
                          columns={columns}
                          actions={rowActions}
                          onOpen={
                            onOpenRow
                              ? () => {
                                  onOpenRow(row);
                                }
                              : undefined
                          }
                          data-testid={cardTestId?.(row)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <DataTable
            caption={caption}
            showCaption={showCaption}
            columns={tableColumns}
            {...(groups ? { groups } : { rows: rows ?? [] })}
            rowKey={rowKey}
            rowTestId={rowTestId}
            rowClassName={rowClassName}
            expansion={expansion}
            sort={sort}
            onSortChange={onSortChange}
            sortMode={sortMode}
            verticalAlign={verticalAlign}
            density={density}
            stickyHeader={stickyHeader}
            stickyFirstColumn={stickyFirstColumn}
            emptyState={emptyState}
            loading={Boolean(loading)}
            loadingLabel={
              loading === "silent" ? "" : (loadingLabel ?? `Loading ${caption.toLowerCase()}`)
            }
            data-testid={testId}
          />
        ))}
    </div>
  );
}
